/**
 * Chord Inversion Drill — free-practice mode.
 *
 * The chord quality is shown on screen; the user identifies which inversion
 * (bass note position) they heard.
 * Pure — no I/O, no audio APIs, no DOM.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import { buildChord } from "./utils.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type InversionChordType =
  | "maj" | "min" | "dim" | "aug"
  | "maj7" | "min7" | "dom7" | "min7b5" | "dim7";

/** Inversion index: 0 = root position, 1 = 1st, 2 = 2nd, 3 = 3rd (7ths only). */
export type InversionIndex = 0 | 1 | 2 | 3;

export interface ChordInversionExerciseConfig {
  /** Chord types to draw from, ≥1. */
  chordTypes: InversionChordType[];
  /** Inversion positions to include (filtered per round to valid range). */
  inversions: InversionIndex[];
  voicing: "arpeggiated" | "blocked" | "mixed";
  tonic: "fixed" | "random";
  fixedTonicMidi: number;
  drillMode: "graded" | "listening";
  fsrsEnabled: boolean;
}

export interface ChordInversionExerciseRound {
  chordType: InversionChordType;
  inversionIndex: InversionIndex;
  rootMidi: number;
  closeMidis: number[];
  playedMidis: number[];
  /** All valid inversions for this chord type — shown as answer buttons. */
  answerInversions: InversionIndex[];
  effectiveVoicing: "arpeggiated" | "blocked";
  audioPlan: AudioPlan;
  teachAudio: AudioPlan;
}

// ─── Pool metadata ────────────────────────────────────────────────────────────

export interface InversionChordInfo {
  longName: string;
  shortSymbol: string;
  group: "triads" | "sevenths";
  /** Number of distinct chord tones (= max inversion + 1). */
  toneCount: 3 | 4;
}

export const INVERSION_CHORD_POOL: Record<InversionChordType, InversionChordInfo> = {
  maj:    { longName: "Major",          shortSymbol: "maj",   group: "triads",   toneCount: 3 },
  min:    { longName: "Minor",          shortSymbol: "min",   group: "triads",   toneCount: 3 },
  dim:    { longName: "Diminished",     shortSymbol: "dim",   group: "triads",   toneCount: 3 },
  aug:    { longName: "Augmented",      shortSymbol: "aug",   group: "triads",   toneCount: 3 },
  maj7:   { longName: "Major 7th",      shortSymbol: "maj7",  group: "sevenths", toneCount: 4 },
  min7:   { longName: "Minor 7th",      shortSymbol: "min7",  group: "sevenths", toneCount: 4 },
  dom7:   { longName: "Dominant 7th",   shortSymbol: "dom7",  group: "sevenths", toneCount: 4 },
  min7b5: { longName: "Half-dim.",      shortSymbol: "m7♭5",  group: "sevenths", toneCount: 4 },
  dim7:   { longName: "Full dim.",      shortSymbol: "°7",    group: "sevenths", toneCount: 4 },
};

export const INVERSION_CHORD_TYPES_ORDERED: readonly InversionChordType[] = [
  "maj", "min", "dim", "aug",
  "maj7", "min7", "dom7", "min7b5", "dim7",
];

export interface InversionChordGroup {
  label: string;
  symbols: InversionChordType[];
}

export const INVERSION_CHORD_GROUPS: readonly InversionChordGroup[] = [
  { label: "Triads",  symbols: ["maj", "min", "dim", "aug"] },
  { label: "7th chords", symbols: ["maj7", "min7", "dom7", "min7b5", "dim7"] },
];

// ─── Inversion labels ─────────────────────────────────────────────────────────

export const INVERSION_NAMES: Record<InversionIndex, string> = {
  0: "Root position",
  1: "1st inversion",
  2: "2nd inversion",
  3: "3rd inversion",
};

export const INVERSION_SUBLABELS: Record<InversionIndex, string> = {
  0: "root in bass",
  1: "3rd in bass",
  2: "5th in bass",
  3: "7th in bass",
};

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_CHORD_INVERSION_EXERCISE_CONFIG: ChordInversionExerciseConfig = {
  chordTypes: ["maj", "min"],
  inversions: [0, 1, 2],
  voicing: "arpeggiated",
  tonic: "random",
  fixedTonicMidi: 60,
  drillMode: "graded",
  fsrsEnabled: false,
};

// ─── FSRS item ID ─────────────────────────────────────────────────────────────

/**
 * maj/min triads map to existing curriculum nodes.
 * dim/aug triads and all 7th chord inversions go to the drill namespace.
 */
export function inversionDrillItemId(
  chordType: InversionChordType,
  inversionIndex: InversionIndex,
): string {
  if ((chordType === "maj" || chordType === "min") && inversionIndex <= 2) {
    return `triad-inversions::${chordType}::${inversionIndex}`;
  }
  return `exercise-inversion::${chordType}::inv${inversionIndex}`;
}

// ─── Stats key ────────────────────────────────────────────────────────────────

/**
 * Format: `inversion-drill|${chordType}|${inversionIndex}|${tonic}`
 * where tonic is MIDI number for fixed or "rng" for random.
 */
export function inversionDrillStatsKey(
  chordType: InversionChordType,
  inversionIndex: InversionIndex,
  config: Pick<ChordInversionExerciseConfig, "tonic" | "fixedTonicMidi">,
): string {
  const tonicPart = config.tonic === "random" ? "rng" : String(config.fixedTonicMidi);
  return `inversion-drill|${chordType}|${inversionIndex}|${tonicPart}`;
}

// ─── Audio helpers ────────────────────────────────────────────────────────────

function applyInversion(closeMidis: number[], inversionIndex: number): number[] {
  if (inversionIndex === 0) return closeMidis;
  const rotated = [...closeMidis];
  for (let i = 0; i < inversionIndex; i++) {
    rotated[i] = rotated[i]! + 12;
  }
  return rotated.sort((a, b) => a - b);
}

function buildArpBlock(builder: AudioPlanBuilder, midis: number[]): void {
  for (const note of midis) {
    builder.addNote(note, 0.55, 0.20, "melody");
    builder.advance(0.30);
  }
  builder.advance(0.4);
  builder.addChord(midis, 1.4, 0.13, "harmony");
  builder.advance(1.5);
}

function buildDoubleBlock(builder: AudioPlanBuilder, midis: number[]): void {
  builder.addChord(midis, 0.6, 0.13, "harmony");
  builder.advance(0.7);
  builder.addChord(midis, 1.4, 0.13, "harmony");
  builder.advance(1.5);
}

function buildInversionTeachAudio(playedMidis: number[]): AudioPlan {
  const bass = playedMidis[0]!;
  const builder = new AudioPlanBuilder();
  // Slow arpeggio.
  for (const note of playedMidis) {
    builder.addNote(note, 0.7, 0.22, "melody");
    builder.advance(0.45);
  }
  builder.advance(0.4);
  // Emphasize the bass note alone (the inversion-defining tone).
  builder.addNote(bass, 1.4, 0.24, "target");
  builder.advance(1.6);
  // Full block chord.
  builder.addChord(playedMidis, 1.5, 0.13, "harmony");
  builder.advance(1.6);
  return builder.build();
}

// ─── Main builder ─────────────────────────────────────────────────────────────

const CONCRETE_VOICINGS: ReadonlyArray<"arpeggiated" | "blocked"> = ["arpeggiated", "blocked"];

export function buildChordInversionExerciseRound(
  config: ChordInversionExerciseConfig,
  seed: number,
): ChordInversionExerciseRound {
  const rng = new Rng(seed);

  const chordType = rng.pick(config.chordTypes as InversionChordType[]);
  const { toneCount } = INVERSION_CHORD_POOL[chordType];
  const maxInversion = (toneCount - 1) as InversionIndex;

  // Filter configured inversions to valid range for this chord type.
  const validConfigInversions = (config.inversions as InversionIndex[]).filter(
    (i) => i <= maxInversion,
  );
  // Fall back to all valid if no intersection.
  const pickableInversions: InversionIndex[] =
    validConfigInversions.length > 0
      ? validConfigInversions
      : ([0, 1, 2, 3] as InversionIndex[]).slice(0, toneCount) as InversionIndex[];

  const inversionIndex = rng.pick(pickableInversions);

  const effectiveVoicing: "arpeggiated" | "blocked" =
    config.voicing === "mixed"
      ? rng.pick(CONCRETE_VOICINGS as Array<"arpeggiated" | "blocked">)
      : config.voicing;

  // Root in F3–C4 range (MIDI 53–60).
  const rootMidi =
    config.tonic === "fixed"
      ? config.fixedTonicMidi
      : 53 + rng.int(8);

  const closeMidis = buildChord(rootMidi, chordType);
  const playedMidis = applyInversion(closeMidis, inversionIndex);

  // Answer inversions: all valid positions for this chord type (0..maxInversion).
  const answerInversions: InversionIndex[] = (
    [0, 1, 2, 3] as InversionIndex[]
  ).slice(0, toneCount) as InversionIndex[];

  const builder = new AudioPlanBuilder();
  if (effectiveVoicing === "arpeggiated") {
    buildArpBlock(builder, playedMidis);
  } else {
    buildDoubleBlock(builder, playedMidis);
  }

  return {
    chordType,
    inversionIndex,
    rootMidi,
    closeMidis,
    playedMidis,
    answerInversions,
    effectiveVoicing,
    audioPlan: builder.build(),
    teachAudio: buildInversionTeachAudio(playedMidis),
  };
}
